// Lead contact phones are stored as "(302) 555-1234", with " ext. 12" for
// extensions and several numbers joined with " / ". Legacy data has 600+
// spellings ("302.555.1234", "+1 302-555-1234 // 302 555 9999", "NA"…) —
// everything is normalised to this format on save.
// Keep in sync with Backend/utility/phone.js (normalizePhoneText).

// Placeholder text typed into phone fields that means "no number".
const PHONE_PLACEHOLDERS = new Set([
  "na", "n/a", "n.a.", "null", "undefined", "none", "nil", "not found",
  "not available", "-", "--", "`", ".",
]);

// One NANP number inside free text: optional +1/1 prefix, 3-3-4 digits with any
// common separators, optional extension. Digit lookarounds keep it from biting
// into longer digit runs.
const NANP_IN_TEXT = /(?<![\d+])(?:\+?\s*1[\s.-]*)?\(?(\d{3})\)?[\s.-]*(\d{3})[\s.-]*(\d{4})(?:\s*,?\s*(?:x|ext|extn|extension|#)\.?:?\s*(\d{1,6}))?(?!\d)/gi;

// Zero-width characters pasted in from web pages / spreadsheets.
const INVISIBLE_CHARS = /[\u200B-\u200D\u2060\uFEFF]/g;

// What may sit between several numbers in one field ("//", "/", ",", "or" ...).
const NUMBER_SEPARATORS = /^(?:[\s/\\,;|&:.\-]|\bor\b|\band\b)*$/i;

// A "+" not followed by 1 (e.g. "+44"), or a leading 00/011 dialling prefix.
const INTERNATIONAL_PREFIX = /\+\s*[02-9]|^(?:00|011)/;

const formatNanp = (area, prefix, line, ext) =>
  `(${area}) ${prefix}-${line}${ext ? ` ext. ${ext}` : ""}`;

/**
 * Normalise a free-text phone field. Returns { value, kind } where kind is
 * empty | placeholder | number | multiple | labeled | international | unrecognized.
 * International and unrecognised input is returned as typed (trimmed).
 */
export function normalizePhoneText(raw) {
  if (raw === null || raw === undefined) return { value: raw, kind: "empty" };
  const text = String(raw).replace(INVISIBLE_CHARS, "").replace(/\u00A0/g, " ").trim();
  if (!text) return { value: text === raw ? raw : "", kind: "empty" };
  if (PHONE_PLACEHOLDERS.has(text.toLowerCase())) return { value: "", kind: "placeholder" };
  if (INTERNATIONAL_PREFIX.test(text)) return { value: text, kind: "international" };

  const numbers = [];
  const leftover = text.replace(NANP_IN_TEXT, (_, area, prefix, line, ext) => {
    numbers.push(formatNanp(area, prefix, line, ext));
    return " ";
  });
  if (numbers.length === 0) return { value: text, kind: "unrecognized" };

  if (NUMBER_SEPARATORS.test(leftover)) {
    const unique = [...new Set(numbers)];
    return { value: unique.join(" / "), kind: unique.length > 1 ? "multiple" : "number" };
  }
  const inPlace = text
    .replace(NANP_IN_TEXT, (_, area, prefix, line, ext) => formatNanp(area, prefix, line, ext))
    .replace(/\s{2,}/g, " ");
  return { value: inPlace, kind: "labeled" };
}

/** Canonical form of a phone field. Unparseable input is kept as typed. */
export const normalizePhone = (raw) => normalizePhoneText(raw).value;

/** Shown in empty phone inputs so the team sees the stored format. */
export const PHONE_PLACEHOLDER = "(302) 555-1234";

// Input the as-you-type formatter handles: digits, phone punctuation and an
// optional trailing extension. Anything else (a second number after "/", labels,
// letters) is left as typed and normalised when the field loses focus.
const FORMATTABLE_INPUT = /^[\d\s().+-]*(?:\s*(?:x|ext|extn|extension)\.?:?\s*\d*)?$/i;

/**
 * Format a phone number while it is typed: "5039" → "(503) 9", "5039224847" →
 * "(503) 922-4847", extra digits → " ext. 12". A leading 1 is shown as "+1 " until
 * blur (US area codes never start with 1). Never adds trailing punctuation, so
 * the next keystroke or backspace always acts on a digit.
 */
export function formatPhoneAsYouType(raw) {
  const text = String(raw ?? "");
  if (!text.trim() || !FORMATTABLE_INPUT.test(text) || INTERNATIONAL_PREFIX.test(text)) return text;
  let digits = text.replace(/\D/g, "");
  const hasCountryCode = digits.startsWith("1");
  if (hasCountryCode) digits = digits.slice(1);
  const area = digits.slice(0, 3);
  const prefix = digits.slice(3, 6);
  const line = digits.slice(6, 10);
  const ext = digits.slice(10);
  const typedExtMarker = /[a-z]/i.test(text);
  const parts = [];
  if (hasCountryCode) parts.push("+1");
  if (area) parts.push(`(${area}${prefix ? ")" : ""}`);
  let out = parts.join(" ");
  if (prefix) out += ` ${prefix}`;
  if (line) out += `-${line}`;
  if (ext || (typedExtMarker && line.length === 4)) out += ` ext. ${ext}`;
  return out || text;
}

/** Caret position just after the `count`-th digit of `text` (end if fewer digits). */
export function caretAfterDigits(text, count) {
  if (count <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < text.length; i++) {
    if (/\d/.test(text[i]) && ++seen === count) return i + 1;
  }
  return text.length;
}

/**
 * Digits to dial for a stored phone field: the first US/Canada number without
 * its extension, so "(302) 555-1234 ext. 12 / (302) 555-9999" dials 3025551234.
 * Falls back to every digit for international / unrecognised values.
 */
export function phoneToDial(raw) {
  if (!raw) return "";
  const text = String(raw);
  if (!INTERNATIONAL_PREFIX.test(text)) {
    const first = new RegExp(NANP_IN_TEXT.source, "i").exec(text);
    if (first) return `${first[1]}${first[2]}${first[3]}`;
  }
  return text.replace(/\D/g, "");
}
